import React, { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import {
  ApolloClient,
  InMemoryCache,
  from,
  HttpLink,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { ApolloProvider } from "@apollo/client/react";
import HelloSign from "hellosign-embedded";

import "./App.css";
import { Shell } from "@shared/components/Shell";
import { ToastProvider } from "@shared/components/Toast";
import {
  ThemeProvider,
  AuthProvider,
  getAccessTokenForRequest,
} from "@shared/context";
import { apolloURL, adminApolloURL } from "./constants/apollo.constants";
import { RoleRoute, CustomerRouteGuard } from "./routes";
import { RealtimeProvider } from "./context/RealtimeContext";

// Eagerly loaded: auth pages (first interaction)
import Login from "./components/login/login";
import Register from "./components/register/register";
import ForgotPassword from "./components/forgot-password/forgot-password.component";
import ResetPassword from "./components/reset-password/reset-password.component";
import LockedPage from "./components/locked-page/locked-page.component";
import AuthCallback from "./pages/auth/callback";

// Lazy loaded: authenticated portal pages (code-splitting for bundle size)
const Overview = lazy(() => import("./components/overview/overview"));
const AccountDetails = lazy(() => import("./components/account-details/account-details"));
const TransactionsHistory = lazy(() => import("./components/transcations-history/transactions-history"));
const BillingPage = lazy(() => import("./pages/billing"));
const Checkout = lazy(() => import("./components/checkout/checkout"));
const CertificateType = lazy(() => import("./components/certificate-type/certificate-type"));
const CreateTransaction = lazy(() => import("./components/create-transaction/create-transaction"));
const PaymentPage = lazy(() => import("./pages/payment"));
const TransactionComplete = lazy(() => import("./components/transaction-complete/transaction-complete.component"));
const PostTransactionDetails = lazy(() => import("./components/post-transaction-details/post-transaction-details"));
const CartPage = lazy(() => import("./pages/cart"));
const CartPaymentPage = lazy(() => import("./pages/cart-payment"));
const CartVerificationPage = lazy(() => import("./pages/cart-verification"));
const CartPhoneVerificationPage = lazy(() => import("./pages/cart-phone-verification"));
const PaymentConfirmPage = lazy(() => import("./pages/payment-confirm"));
const ClaimPage = lazy(() => import("./pages/claim"));
const Suspicious = lazy(() => import('./components/suspicious/suspicious.component'));

// Staff portal pages (Case Manager)
const CaseManagerDashboard = lazy(() => import("./pages/case-manager/CaseManagerDashboard"));

// Staff portal pages (Compliance)
const ComplianceDashboard = lazy(() => import("./pages/compliance/ComplianceDashboard"));

// Admin portal pages
// AdminTransactions (legacy /admin/transactions standalone page) is being
// replaced by the All TX lane inside the role-appropriate dashboard. The
// component is kept around as dead code for one cycle in case we need to
// roll back via the route swap; strategy register has the cleanup PR.

// Operations portal pages
const DeliveryDashboard = lazy(() => import("./pages/admin/delivery/DeliveryDashboard"));
// Dev-only preview routes (harnesses with mock data; gated on
// import.meta.env.DEV in the route registration). Lazy-imported so the
// chunk only loads when the preview route is hit.
const CartRowPreview = lazy(() => import("./pages/preview/cart-row-preview"));
const QuestionnaireDisclaimerPreview = lazy(
  () => import("./pages/preview/questionnaire-disclaimer-preview"),
);
const SupportingDocumentsPreview = lazy(
  () => import("./pages/preview/supporting-documents-preview"),
);
const RequirementsIconographyPreview = lazy(
  () => import("./pages/preview/requirements-iconography-preview"),
);
const ShippingPolicyPreview = lazy(
  () => import("./pages/preview/shipping-policy-preview"),
);
const AdminRefundRedesignPreview = lazy(
  () => import("./pages/preview/admin-refund-redesign-preview"),
);
const CopyAuditPreview = lazy(
  () => import("./pages/preview/copy-audit-preview"),
);
const BackFlowPreview = lazy(
  () => import("./pages/preview/back-flow-preview"),
);
const CheckoutRebuildPreview = lazy(
  () => import("./pages/preview/checkout-rebuild-preview"),
);
const DeliveryTabPreview = lazy(
  () => import("./pages/preview/delivery-tab-preview"),
);
const PaymentErrorUxPreview = lazy(
  () => import("./pages/preview/payment-error-ux-preview"),
);
const V2LaunchTier1bcPreview = lazy(
  () => import("./pages/preview/v2-launch-tier1bc-preview"),
);
const LegacyTransactionsRedirect = lazy(() => import("./components/dashboard-redirect/LegacyTransactionsRedirect"));
const AdminUsers = lazy(() => import("./pages/admin/users/users.component"));
const AdminUser = lazy(() => import("./pages/admin/user/user.component"));
const AdminReports = lazy(() => import("./pages/admin/reports/reports.component"));
const AdminSurveys = lazy(() => import("./pages/admin/surveys/surveys.component"));
const AdminAllDiscounts = lazy(() => import("./pages/admin/all-discounts/all-discounts.component"));
const AdminTransferAgents = lazy(() => import("./pages/admin/transfer-agents/transfer-agents.component"));
const AdminTransferAgentDetail = lazy(() => import("./pages/admin/transfer-agents/transfer-agent-detail.component"));
const AdminCreateDiscount = lazy(() => import("./pages/admin/create-discount/create-discount.component"));
const AdminDashboard = lazy(() => import("./pages/admin/dashboard/dashboard.component"));
const TransactionReview = lazy(() => import("./pages/admin/transaction-review/transaction-review.component"));
const AdminNotifications = lazy(() => import("./pages/admin/notifications/notifications.component"));

// Loading fallback for lazy components
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <div className="spinner" aria-label="Loading..." />
  </div>
);

// Dashboard redirect component - sends users to their role-appropriate dashboard
const DashboardRedirect = lazy(() => import("./components/dashboard-redirect/DashboardRedirect"));

const httpLink = new HttpLink({
  uri: apolloURL,
});

// Setup the header for the request.
//
// The token is fetched per operation rather than read from localStorage:
// WorkOS access tokens are short-lived and the AuthKit SDK rotates them on a
// timer, so a token captured at login is stale within minutes. Since the API
// verifies `exp`, a stale token means a failed request and a bounce to
// /login - which is what users saw as being logged out every few minutes.
const middlewareAuthLink = setContext(async (_operation, { headers }) => {
  const token = await getAccessTokenForRequest();

  return {
    headers: {
      ...headers,
      // WorkOS access token, sent as a bearer token.
      authorization: token ? `Bearer ${token}` : null,
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      if (
        message === "Access denied! You don't have permission for this action!"
      ) {
        window.location.replace("/login");
      }
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, extensions ${extensions}`
      );
    });

  if (networkError) console.log(`[Network error]: ${networkError}`);
});

const client = new ApolloClient({
  link: from([errorLink, middlewareAuthLink, httpLink]),
  cache: new InMemoryCache(),
  credentials: "include",
});

// Admin Apollo client — points to /admin endpoint which uses AdminResolver (all data, not user-scoped)
const adminHttpLink = new HttpLink({
  uri: adminApolloURL,
});

export const adminClient = new ApolloClient({
  link: from([errorLink, middlewareAuthLink, adminHttpLink]),
  cache: new InMemoryCache(),
  credentials: "include",
});

// Create the HelloSign Embedded instance.
// Only do this once!
export const helloSignClient = new HelloSign({
  clientId: import.meta.env.VITE_HELLO_SIGN,
  skipDomainVerification: true,
});

/**
 * Portal App (LAYER-CODEBASE-ARCHITECTURE)
 *
 * Marketing pages have been moved to the marketing-site (Astro).
 * This portal now only handles:
 * - Authentication (login, register, password reset)
 * - Authenticated customer routes (accounts, transactions, payments)
 */
function App() {
  return (
    <ThemeProvider defaultTheme="light">
      <ApolloProvider client={client}>
        <AuthProvider>
          <ToastProvider>
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Authentication routes - no Shell wrapper
                      Intentionally minimal: users see a clean, focused login experience
                      without the portal navigation. After auth, they're redirected to
                      authenticated routes which include the full Shell with header/nav. */}
                  <Route path="login" element={<Login />} />
                  <Route path="forgot-password" element={<ForgotPassword />} />
                  <Route path="reset-password" element={<ResetPassword />} />
                  <Route path="join" element={<Register />} />
                  {/* WorkOS AuthKit callback route */}
                  <Route path="auth/callback" element={<AuthCallback />} />
                  <Route path="locked-user" element={<LockedPage />} />
                  <Route path="suspicious/:id" element={<Suspicious />} />

                  {/* Preview routes — dev-only UX inspection harnesses with
                      mock data. No auth, no GraphQL. Vite's import.meta.env.DEV
                      is true for `npm run dev` and false for production
                      builds, so these routes never reach prod even though
                      they're imported. See pages/preview/ for the
                      individual harness components. */}
                  {import.meta.env.DEV && (
                    <Route path="preview/cart-row" element={<CartRowPreview />} />
                  )}
                  {import.meta.env.DEV && (
                    <Route
                      path="preview/questionnaire-disclaimers"
                      element={<QuestionnaireDisclaimerPreview />}
                    />
                  )}
                  {import.meta.env.DEV && (
                    <Route
                      path="preview/supporting-documents"
                      element={<SupportingDocumentsPreview />}
                    />
                  )}
                  {import.meta.env.DEV && (
                    <Route
                      path="preview/requirements-iconography"
                      element={<RequirementsIconographyPreview />}
                    />
                  )}
                  {import.meta.env.DEV && (
                    <Route
                      path="preview/shipping-policy"
                      element={<ShippingPolicyPreview />}
                    />
                  )}
                  {import.meta.env.DEV && (
                    <Route
                      path="preview/admin-refund-redesign"
                      element={<AdminRefundRedesignPreview />}
                    />
                  )}
                  {import.meta.env.DEV && (
                    <Route
                      path="preview/copy-audit"
                      element={<CopyAuditPreview />}
                    />
                  )}
                  {import.meta.env.DEV && (
                    <Route
                      path="preview/back-flow"
                      element={<BackFlowPreview />}
                    />
                  )}
                  {import.meta.env.DEV && (
                    <Route
                      path="preview/checkout-rebuild"
                      element={<CheckoutRebuildPreview />}
                    />
                  )}
                  {import.meta.env.DEV && (
                    <Route
                      path="preview/delivery-tab"
                      element={<DeliveryTabPreview />}
                    />
                  )}
                  {import.meta.env.DEV && (
                    <Route
                      path="preview/payment-error-ux"
                      element={<PaymentErrorUxPreview />}
                    />
                  )}
                  {import.meta.env.DEV && (
                    <Route
                      path="preview/v2-launch-tier1bc"
                      element={<V2LaunchTier1bcPreview />}
                    />
                  )}

                  {/* All authenticated routes share a single Shell.
                      TOS acceptance is captured pre-auth at /join and
                      written by /auth/callback — there's no post-auth
                      acceptance gate. */}
                  <Route element={<Shell />}>
                    {/* Universal dashboard redirect - sends to role-appropriate dashboard */}
                    <Route path="dashboard" element={<DashboardRedirect />} />

                    {/* Customer routes — closes bug 1AC21B (split-brain:
                        COMPLIANCE saw customer welcome + compliance sidebar).
                        Staff roles that land here by accident are bounced to
                        /dashboard, which routes them to their role-appropriate
                        dashboard. CustomerRouteGuard admits staff only when
                        the "view as customer" toggle is active — that's the
                        explicit opt-in signal, not a side door. */}
                    <Route path="accounts" element={<CustomerRouteGuard><Outlet /></CustomerRouteGuard>}>
                      <Route path="me" element={<Overview />} />
                      <Route path="details" element={<AccountDetails />} />
                      <Route path="transactions" element={<Navigate to="/accounts/me" replace />} />
                      <Route path="history" element={<Navigate to="/accounts/me" replace />} />
                      <Route path="billing" element={<BillingPage />} />
                    </Route>
                    {/* Transaction creation flow */}
                    <Route path="transactions">
                      <Route index element={<Navigate to="create/start" replace />} />
                      <Route path="create/start" element={<CreateTransaction />} />
                      <Route path=":transactionId/medallions" element={<CertificateType />} />
                      <Route path=":transactionId/shipping" element={<Checkout />} />
                      <Route path=":transactionId/review" element={<Checkout />} />
                      <Route path=":transactionId/verify" element={<Navigate to="/cart" replace />} />
                      <Route path=":transactionId/payment" element={<Navigate to="/cart" replace />} />
                    </Route>
                    {/* Cart */}
                    <Route path="cart" element={<CartPage />} />
                    <Route path="cart/verification" element={<CartVerificationPage />} />
                    <Route path="cart/phone-verification" element={<CartPhoneVerificationPage />} />
                    <Route path="cart/payment" element={<CartPaymentPage />} />
                    <Route path="payment/confirm" element={<PaymentConfirmPage />} />
                    {/* Post-transaction routes */}
                    <Route path="transactions/:transactionId/details" element={<PostTransactionDetails />} />
                    <Route path="transactions/:transactionId/completed" element={<TransactionComplete />} />

                    {/* Delegation: claim transaction via invitation link */}
                    <Route path="claim/:token" element={<ClaimPage />} />

                    {/* Case Manager dashboard — strict to CM (and ADMIN/SUPER_ADMIN
                        by hierarchy). Compliance does NOT see this dashboard;
                        they have their own at /compliance/dashboard. */}
                    <Route
                      path="case-manager/*"
                      element={<RoleRoute allowedRoles={['CASE_MANAGER', 'ADMIN', 'SUPER_ADMIN']}><ApolloProvider client={adminClient}><RealtimeProvider><Outlet /></RealtimeProvider></ApolloProvider></RoleRoute>}
                    >
                      <Route path="dashboard" element={<CaseManagerDashboard />} />
                      <Route index element={<Navigate to="dashboard" replace />} />
                    </Route>

                    {/* Compliance dashboard — strict to Compliance (and
                        ADMIN/SUPER_ADMIN by hierarchy). CM does NOT see this
                        dashboard. */}
                    <Route
                      path="compliance/*"
                      element={<RoleRoute allowedRoles={['COMPLIANCE', 'ADMIN', 'SUPER_ADMIN']}><ApolloProvider client={adminClient}><RealtimeProvider><Outlet /></RealtimeProvider></ApolloProvider></RoleRoute>}
                    >
                      <Route path="dashboard" element={<ComplianceDashboard />} />
                      <Route index element={<Navigate to="dashboard" replace />} />
                    </Route>

                    {/* Shared transaction surfaces — STAFF tier.
                        All Transactions list, single-transaction admin view,
                        and the transaction-review workspace are accessible
                        to every staff role (CM, Compliance, Admin, Super
                        Admin) because finding and opening a transaction is
                        the panopticon lookup surface every staff role needs. */}
                    <Route
                      path="admin/*"
                      element={<RoleRoute accessTier="STAFF"><ApolloProvider client={adminClient}><RealtimeProvider><Outlet /></RealtimeProvider></ApolloProvider></RoleRoute>}
                    >
                      <Route path="transactions" element={<LegacyTransactionsRedirect />} />
                      <Route path="review/:id" element={<TransactionReview />} />
                    </Route>

                    {/* Operations + Admin shared surfaces — OPS_OR_ADMIN tier.
                        Reports and the notifications page are accessible to
                        OPERATIONS so support can run referral-partner reports
                        and audit notification configuration. The notifications
                        page enforces read-only mode for non-SUPER_ADMIN within
                        the component itself (toggles + edits gated separately).

                        Delivery dashboard also lives in this tier — Operations
                        is the primary user (post-compliance fulfillment is
                        their core daily work), with Admin and Super Admin
                        included for oversight. Pure CM and pure Compliance
                        intentionally do NOT see this dashboard. */}
                    <Route
                      path="admin/*"
                      element={<RoleRoute accessTier="OPS_OR_ADMIN"><ApolloProvider client={adminClient}><RealtimeProvider><Outlet /></RealtimeProvider></ApolloProvider></RoleRoute>}
                    >
                      <Route path="delivery" element={<DeliveryDashboard />} />
                      <Route path="reports" element={<AdminReports />} />
                      <Route path="notifications" element={<AdminNotifications />} />
                    </Route>

                    {/* Admin-only routes
                        Protected by ADMIN access tier (Admin + Super Admin only).
                        System-wide configuration: user management, discount
                        creation, dashboard, surveys. */}
                    <Route
                      path="admin/*"
                      element={<RoleRoute accessTier="ADMIN"><ApolloProvider client={adminClient}><RealtimeProvider><Outlet /></RealtimeProvider></ApolloProvider></RoleRoute>}
                    >
                      <Route path="dashboard" element={<AdminDashboard />} />
                      <Route path="users" element={<AdminUsers />} />
                      <Route path="user/:id" element={<AdminUser />} />
                      <Route path="surveys" element={<AdminSurveys />} />
                      <Route path="discount-codes" element={<AdminAllDiscounts />} />
                      <Route path="create-discount" element={<AdminCreateDiscount />} />
                      <Route path="transfer-agents" element={<AdminTransferAgents />} />
                      <Route path="transfer-agents/new" element={<AdminTransferAgentDetail mode="create" />} />
                      <Route path="transfer-agents/:uuid" element={<AdminTransferAgentDetail />} />
                      <Route index element={<Navigate to="dashboard" replace />} />
                    </Route>
                  </Route>

                  {/* Unauthorized page */}
                  <Route path="unauthorized" element={
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                      <h1>Unauthorized</h1>
                      <p>You do not have permission to access this page.</p>
                      <a href="/login">Return to Login</a>
                    </div>
                  } />

                  {/* Default redirect - send to login for authentication */}
                  <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </ToastProvider>
        </AuthProvider>
      </ApolloProvider>
    </ThemeProvider>
  );
}

export default App;
